import { useTranslations } from "@zapier/i18n";
import { ChangeEvent, useState } from "react";

import {
  Button,
  Field,
  Link,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  TextInput,
} from "@zapier/design-system";

import { useAutoSelectInputText } from "@zapier/shared";
import {
  getMessageFromNetworkError,
  useRenameAuthentication,
} from "@zapier/client";
import { useNotification } from "@zapier/utils";

import styles from "./ConnectionsRenameModal.module.css";

interface Props {
  id?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialValue?: string;
}

interface ContentProps extends Props {
  onSubmit: (title: string) => void;
}

function ConnectionRenameModalContent({
  initialValue,
  isOpen,
  setIsOpen,
  onSubmit,
}: ContentProps) {
  const t = useTranslations("connections-rename-modal");
  const nameInputIdentifier = "rename-connection-name";

  const [value, setValue] = useState(initialValue || "");
  const isValid = value.length > 0 && value.length <= 255;

  useAutoSelectInputText({
    isOpen,
    inputSelector: `#${nameInputIdentifier}`,
    value: initialValue,
  });

  const submit = () => {
    if (!isValid) {
      return;
    }
    if (value !== initialValue) {
      onSubmit(value);
    }
  };

  return (
    <ModalDialogRoot
      isOpen={true}
      onOpenChange={setIsOpen}
      className={styles.renameProjectModal}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={submit}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            if (!isValid || value === initialValue) return;
            return event.key === "Enter" && submit();
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("headings.rename-connection", {
            initialValue: initialValue || "",
            hasInitialValue: initialValue ? "true" : "false",
          })}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          <Field
            label={t("labels.name")}
            inputId={nameInputIdentifier}
            isRequired={true}
            renderInput={(inputProps) => (
              <TextInput
                {...inputProps}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when the modal opens
                autoFocus={true}
                id={nameInputIdentifier}
                isErrored={!isValid}
                defaultValue={initialValue}
                isFullWidth={true}
                maxLength={255}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setValue(event.target?.value || "")
                }
                placeholder={t("placeholders.untitled-connection")}
              />
            )}
          />
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button
            isDisabled={!isValid || value === initialValue}
            onPress={submit}
            type="submit"
          >
            {t("buttons.save")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export default function ConnectionRenameModal({
  id,
  initialValue = "",
  isOpen,
  setIsOpen,
}: Props) {
  const t = useTranslations("connections-rename-modal");

  const { createNotification } = useNotification();
  const { rename } = useRenameAuthentication();

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const submit = (name: string) => {
    if (!id) {
      return;
    }

    rename(
      { name, id },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t.rich("notifications.rename-success-with-undo", {
                  name,
                  undoLink: (chunks) => (
                    <Link onPress={() => undoSubmit(initialValue)}>
                      {chunks}
                    </Link>
                  ),
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("notifications.unable-to-rename-title", {
              initialValue: initialValue ?? "",
            }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    setIsOpen(false);
  };

  const undoSubmit = (name: string) => {
    if (!id) {
      return;
    }

    rename(
      { name, id },
      {
        onSuccess: () => {
          createNotification({
            message: <Text>{t("messages.name-restored", { name: name })}</Text>,
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("messages.unable-to-restore-title"),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return null;
  }

  return (
    <ConnectionRenameModalContent
      id={id}
      initialValue={initialValue}
      isOpen={isOpen}
      onSubmit={submit}
      setIsOpen={setIsOpen}
    />
  );
}
